/**
 * Event page (/events/:eventId): details, the songs to practise (editors choose them), my RSVP +
 * note + check-in, headcount per section, message board; editors also get roster / edit / cancel /
 * delete actions.
 */
import { CalendarX2, ClipboardCheck, WifiOff } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { isEventGone, useEvent, useServerNow } from '../api/calendar';
import { Page } from '../components/layout';
import { CenteredSpinner, EmptyState, ErrorBox } from '../components/ui';
import { useT } from '../i18n';
import { CommentBoard } from './calendar/CommentBoard';
import { EventAdminActions } from './calendar/EventAdminActions';
import { CancelledBanner, EventDetailsCard, MyParticipationCard } from './calendar/EventDetails';
import { HeadcountCard } from './calendar/HeadcountCard';
import { useQueuedRejectionToasts } from './calendar/participation';
import { errorText, useIsEditor } from './calendar/shared';
import { ProgramCard } from '../practice/ProgramCard';

export default function EventScreen() {
  const { eventId = '' } = useParams();
  const t = useT();
  const editor = useIsEditor();
  const now = useServerNow(30_000);
  const q = useEvent(eventId);
  // On 404/403 TanStack keeps the old (persisted) data next to the error: the event was deleted or
  // is no longer visible to me, so never show it (its buttons would only fail).
  const gone = isEventGone(q.error);
  const ev = gone ? undefined : q.data;
  const location = useLocation();
  useQueuedRejectionToasts();

  // "#spjall" links (from the home card) scroll to the message board once it is rendered.
  const hasEvent = !!ev;
  useEffect(() => {
    if (!hasEvent || location.hash !== '#spjall') return;
    const id = requestAnimationFrame(() => document.getElementById('spjall')?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(id);
  }, [hasEvent, location.hash]);

  if (!ev) {
    return (
      <Page title={t('calendar.event.title')} back="/">
        {q.isPending && q.fetchStatus === 'paused' ? (
          <EmptyState icon={WifiOff} title={t('common.offline')}>
            {t('common.no_data_offline')}
          </EmptyState>
        ) : q.isPending ? (
          <CenteredSpinner label={t('common.loading')} />
        ) : gone ? (
          <EmptyState icon={CalendarX2} title={t('error.not_found')}>
            {t('calendar.event.not_found')}
          </EmptyState>
        ) : (
          <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
            {errorText(t, q.error)}
          </ErrorBox>
        )}
      </Page>
    );
  }

  return (
    <Page
      title={t('calendar.event.title')}
      back="/"
      wide
      actions={
        editor ? (
          <Link
            to={`/events/${ev.id}/roster`}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[var(--accent-soft)] px-3 font-semibold text-[var(--accent)]"
            aria-label={t('calendar.actions.roster')}
          >
            <ClipboardCheck className="size-5" aria-hidden />
            <span className="hidden sm:inline">{t('calendar.actions.roster')}</span>
          </Link>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-5">
        <CancelledBanner ev={ev} />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-start">
          <div className="flex flex-col gap-5">
            <EventDetailsCard ev={ev} now={now} />
            <ProgramCard ev={ev} editor={editor} />
            <MyParticipationCard key={ev.id} ev={ev} now={now} />
            <HeadcountCard ev={ev} />
            {editor ? <EventAdminActions ev={ev} /> : null}
          </div>
          <CommentBoard key={ev.id} ev={ev} now={now} />
        </div>
      </div>
    </Page>
  );
}
